import { useState } from "react";
import type { Epic } from "@/lib/api/types";
import { EntityCard } from "@/components/ui/entity-card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Folder, Trash2, Pencil, Archive, ArchiveRestore, User, CheckCircle2 } from "lucide-react";
import { ComplianceBadge } from "@/components/ui/compliance-badge";
import { useDeleteEpic, useArchiveEpic, useUnarchiveEpic, useMarkEpicImplemented } from "@/hooks/queries/use-epics";
import { ApiError } from "@/lib/api/client";
import { getTeamHueColor } from "@/lib/team-hue";

interface EpicCardProps {
  epic: Epic;
  onEdit?: (epic: Epic) => void;
}

export function EpicCard({ epic, onEdit }: EpicCardProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showImplementDialog, setShowImplementDialog] = useState(false);
  const [error, setError] = useState("");
  const deleteEpic = useDeleteEpic();
  const archiveEpic = useArchiveEpic();
  const unarchiveEpic = useUnarchiveEpic();
  const markImplemented = useMarkEpicImplemented();

  const handleDelete = async () => {
    setError("");
    try {
      await deleteEpic.mutateAsync(epic.id);
      setShowDeleteDialog(false);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        const data = err.data as { error?: { message?: string } } | undefined;
        setError(data?.error?.message || `Error ${err.response.status}: Failed to delete epic`);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Failed to delete epic");
      }
    }
  };

  const handleArchive = async () => {
    setError("");
    try {
      await archiveEpic.mutateAsync(epic.id);
      setShowArchiveDialog(false);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        const data = err.data as { error?: { message?: string } } | undefined;
        setError(data?.error?.message || `Error ${err.response.status}: Failed to archive epic`);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Failed to archive epic");
      }
    }
  };

  const handleUnarchive = async () => {
    setError("");
    try {
      await unarchiveEpic.mutateAsync(epic.id);
    } catch (err: unknown) {
      console.error("Unarchive epic error:", err);
    }
  };

  const handleMarkImplemented = async () => {
    setError("");
    try {
      await markImplemented.mutateAsync(epic.id);
      setShowImplementDialog(false);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        const data = err.data as { error?: { message?: string } } | undefined;
        setError(data?.error?.message || `Error ${err.response.status}: Failed to mark epic as implemented`);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Failed to mark epic as implemented");
      }
    }
  };

  const hueColor = getTeamHueColor(epic.team?.name);

  // Clean description — strip markdown headers
  const cleanDesc = epic.description
    ?.replace(/^#+\s+/gm, '')
    .replace(/\n/g, ' ')
    .trim();

  // Smart relative time: hours if < 24h, days otherwise
  const lastEditedRaw = epic.updatedAt ? new Date(epic.updatedAt) : new Date(epic.createdAt);
  const hoursAgo = Math.floor((Date.now() - lastEditedRaw.getTime()) / 3_600_000);
  const lastEdited = hoursAgo < 1
    ? "just now"
    : hoursAgo < 24
      ? `${hoursAgo} hour${hoursAgo === 1 ? "" : "s"} ago`
      : `${Math.floor(hoursAgo / 24)} day${Math.floor(hoursAgo / 24) === 1 ? "" : "s"} ago`;

  // Real progress from feature completion data
  const totalFeatures = epic._count?.features ?? 0;
  const completedFeatures = epic.completedFeatureCount ?? 0;
  const featureProgress = totalFeatures > 0 ? Math.round((completedFeatures / totalFeatures) * 100) : 0;

  const isPersonalScope = !epic.team;

  const menuItems = epic.isArchived ? (
    <>
      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); void handleUnarchive(); }}>
        <ArchiveRestore className="h-4 w-4 mr-2" />
        Restore
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={(e) => { e.stopPropagation(); setShowDeleteDialog(true); }}>
        <Trash2 className="h-4 w-4 mr-2" />
        Delete Permanently
      </DropdownMenuItem>
    </>
  ) : (
    <>
      {onEdit && (
        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(epic); }}>
          <Pencil className="h-4 w-4 mr-2" />
          Edit
        </DropdownMenuItem>
      )}
      {epic.state !== 'implemented' && !epic.currentFeatureIdentifier && totalFeatures > 0 && (
        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setError(""); setShowImplementDialog(true); }}>
          <CheckCircle2 className="h-4 w-4 mr-2" />
          Mark as Implemented
        </DropdownMenuItem>
      )}
      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setShowArchiveDialog(true); }}>
        <Archive className="h-4 w-4 mr-2" />
        Archive
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={(e) => { e.stopPropagation(); setShowDeleteDialog(true); }}>
        <Trash2 className="h-4 w-4 mr-2" />
        Delete
      </DropdownMenuItem>
    </>
  );

  return (
    <>
      <EntityCard
        href={`/epics/${epic.id}`}
        hueColor={hueColor}
        icon={<Folder className="h-5 w-5" style={{ color: hueColor }} />}
        title={epic.identifier ? `${epic.identifier} — ${epic.name}` : epic.name}
        subtitle={cleanDesc}
        badge={
          epic.isArchived ? (
            <Badge variant="secondary" className="gap-1 text-xs">
              <Archive className="h-3 w-3" />
              Archived
            </Badge>
          ) : epic.state === 'implemented' ? (
            <Badge variant="secondary" className="gap-1 text-xs text-emerald-400 border-emerald-400/30 bg-emerald-400/10">
              <CheckCircle2 className="h-3 w-3" />
              Implemented
            </Badge>
          ) : isPersonalScope ? (
            <Badge variant="outline" className="gap-1 text-xs text-muted-foreground">
              <User className="h-3 w-3" />
              Personal
            </Badge>
          ) : undefined
        }
        user={epic.creator ? { name: epic.creator.name, avatarUrl: epic.creator.avatarUrl ?? null } : null}
        meta={epic.currentFeatureIdentifier ?? epic.team?.key}
        secondaryMeta={totalFeatures > 0 ? `${completedFeatures}/${totalFeatures}` : undefined}
        progress={totalFeatures > 0 ? featureProgress : undefined}
        footer={
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              Edited {lastEdited}
            </span>
            <ComplianceBadge score={epic.complianceScore ?? null} interactive={false} />
          </div>
        }
        menuItems={menuItems}
        dimmed={epic.isArchived}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Epic</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{epic.name}"? This will also
              delete all features and tasks in this epic. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {error && <div className="text-sm text-destructive">{error}</div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => void handleDelete()} disabled={deleteEpic.isPending}>
              {deleteEpic.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive confirmation dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Epic</DialogTitle>
            <DialogDescription>
              Are you sure you want to archive "{epic.name}"? The epic and its features
              will be hidden from the default view but can be restored later.
            </DialogDescription>
          </DialogHeader>
          {error && <div className="text-sm text-destructive">{error}</div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>Cancel</Button>
            <Button onClick={() => void handleArchive()} disabled={archiveEpic.isPending}>
              {archiveEpic.isPending ? "Archiving..." : "Archive"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as Implemented confirmation dialog */}
      <Dialog open={showImplementDialog} onOpenChange={(open) => { if (!open) setError(""); setShowImplementDialog(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as Implemented</DialogTitle>
            <DialogDescription>
              Are you sure you want to mark "{epic.name}" as implemented? This indicates
              that all features have been completed or canceled and the epic is fully delivered.
            </DialogDescription>
          </DialogHeader>
          {error && <div className="text-sm text-destructive">{error}</div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImplementDialog(false)}>Cancel</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => void handleMarkImplemented()}
              disabled={markImplemented.isPending}
            >
              {markImplemented.isPending ? "Marking..." : "Mark as Implemented"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
